/**
 * Filters as chips instead of selects: a quiet segmented control for one-of choices (group by, sort) and facet rows of
 * toggle chips (several values per facet, each with its count). The state lives with the caller (in the URL).
 */
import { useState, type ReactNode } from 'react';
import { cx } from '../design/primitives.tsx';
import { count } from '../lib/format.ts';
import { CheckIcon } from './icons.tsx';

/** A small segmented control: "Group: None · Domain · System". The choice is aria-pressed. */
export function Segmented<T extends string>({ label, ariaLabel, options, value, onChange, className }: {
  label?: string; ariaLabel: string; options: readonly (readonly [T, string])[]; value: T; onChange: (v: T) => void; className?: string;
}) {
  return (
    <div className={cx('flex items-center gap-2 min-w-0', className)}>
      {label && <span className="text-micro text-n7 whitespace-nowrap">{label}</span>}
      <div role="group" aria-label={ariaLabel} className="flex items-center gap-0.5 p-0.5 rounded-full bg-n3 w-fit max-w-full flex-wrap">
        {options.map(([k, text]) => (
          <button key={k} type="button" aria-pressed={value === k} data-value={k} onClick={() => { if (k !== value) onChange(k); }}
            className={cx('h-6 px-2.5 rounded-full border-0 cursor-pointer text-micro whitespace-nowrap transition-colors duration-[var(--t-micro)]',
              value === k ? 'bg-n1 text-n10 font-medium shadow-[0_1px_2px_rgb(0_0_0/0.1)]' : 'bg-transparent text-n8 hover:text-n10')}>
            {text}
          </button>
        ))}
      </div>
    </div>
  );
}

/** One toggle chip of a facet: the value, its count; chosen = accent with a check. */
export function FacetChip({ chosen, count: n, dashed, title, onClick, className, children, ...data }: {
  chosen: boolean; count?: number; dashed?: boolean; title?: string; onClick: () => void; className?: string; children: ReactNode;
  'data-facet'?: string; 'data-key'?: string;
}) {
  const empty = n === 0;
  return (
    <button type="button" aria-pressed={chosen} title={title} onClick={onClick} {...data}
      className={cx('inline-flex items-center gap-1.5 h-7 pl-2.5 pr-2.5 rounded-full cursor-pointer text-small whitespace-nowrap max-w-full min-w-0 border transition-colors duration-[var(--t-micro)]',
        chosen ? 'bg-accent-soft text-accent border-transparent font-medium pl-2'
          : cx('bg-transparent text-n9 hover:bg-n3 hover:text-n10', dashed ? 'border-dashed border-n6' : 'border-n4'),
        empty && 'opacity-50', className)}>
      {chosen && <CheckIcon size={12} className="shrink-0" />}
      <span className="inline-flex items-center gap-1.5 min-w-0">{children}</span>
      {n !== undefined && <span className={cx('tabular-nums text-micro', chosen ? 'text-accent' : 'text-n7')}>{count(n)}</span>}
    </button>
  );
}

/**
 * A row of a facet: a quiet label and its chips. Many values: the first `limit` (and every chosen one) show, then a
 * "+N more" chip that opens the rest in place (and "Fewer" closes them).
 */
export function FacetRow<V extends { key: string }>({ label, facet, values, chosen, limit = 6, chip }: {
  label: string; facet: string; values: readonly V[]; chosen: readonly string[]; limit?: number; chip: (v: V) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const shown = open || values.length <= limit + 1 ? values : values.filter((v, i) => i < limit || chosen.includes(v.key));
  const hidden = values.length - shown.length;
  return (
    <div role="group" aria-label={label} data-facet-row={facet} className="flex items-start gap-x-3 gap-y-1.5 min-w-0">
      <span className="w-[84px] shrink-0 pt-1.5 text-micro text-n7 uppercase tracking-wide font-semibold">{label}</span>
      <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
        {shown.map((v) => <span key={v.key} className="contents">{chip(v)}</span>)}
        {hidden > 0 && (
          <button type="button" onClick={() => setOpen(true)} aria-expanded={false} data-more={facet}
            className="h-7 px-2.5 rounded-full border-0 bg-transparent cursor-pointer text-small text-accent whitespace-nowrap hover:bg-n3">
            +{count(hidden)} more
          </button>
        )}
        {open && values.length > limit + 1 && (
          <button type="button" onClick={() => setOpen(false)} aria-expanded={true} data-more={facet}
            className="h-7 px-2.5 rounded-full border-0 bg-transparent cursor-pointer text-small text-n8 whitespace-nowrap hover:bg-n3 hover:text-n10">
            Fewer
          </button>
        )}
      </div>
    </div>
  );
}
